/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import "./i18n";
import "./utils/fetchAssetsGuard";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router";
import { AppProvider } from "./context/AppContext";
import { AuthSessionProvider } from "./context/AuthSessionContext";
import { NotificationProvider } from "./context/NotificationContext";
import { Provider } from "react-redux";
import store from "./redux/store";

// Dayjs configuration
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es";
import "dayjs/locale/fr";
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.locale("es");

dayjs.updateLocale("es", {
  // Capitalize the first letter of the day and month
  weekdays: [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ],
  months: [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ],
  formats: {
    // Customize the format for the "long date" format to include "de" in the date format
    LLL: "dddd D [de] MMMM, YYYY", // Example: "Miércoles 30 de abril, 2025"
    LL: "D [de] MMMM YYYY",
  },
});

dayjs.updateLocale("en", {
  formats: {
    // Customize the format for the "long date" format to remove time from the date format
    LLL: "dddd, MMMM D, YYYY", // Example: "Wednesday, April 30, 2025"
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <AppProvider>
        <NotificationProvider>
          <BrowserRouter>
            <AuthSessionProvider>
              <App />
            </AuthSessionProvider>
          </BrowserRouter>
        </NotificationProvider>
      </AppProvider>
    </Provider>
  </React.StrictMode>,
);

reportWebVitals();
